import React from "react";
import styled from "styled-components";
import mixins, { media } from "../../../styles/mixins";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { useScheduleQuery } from "../../../hooks/api/useSchedule";
import { useTransferredActivityList } from "../../../hooks/api/useActivityTransfer";
import { showAlert } from "../../../reducer/alertSlice";
import Loading from "../../organisms/Loading";
import PageTemplate from "../../templates/PageTemplate";
import Error from "../../organisms/Error";

/* ───────────── 아이콘 컴포넌트 ───────────── */

// 신청하기 아이콘 (문서 + 체크)
const IconDocumentApply = () => (
	<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28" fill="none">
		<path d="M23.3333 9.33331V2.33331H10.5L6.99998 5.83331L4.66665 8.16665V25.6666H9.33331M10.5 2.33331V8.16665H4.66665" stroke="black" strokeWidth="1.16667"/>
		<circle cx="19.8333" cy="19.8333" r="7" stroke="black" strokeWidth="1.16667"/>
		<path d="M16.445 20L18.7783 22.3333L23.445 17.6667" stroke="black" strokeWidth="1.16667"/>
	</svg>
);

// 신청조회 아이콘 (문서 + 돋보기)
const IconDocumentSearch = () => (
	<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28" fill="none">
		<path d="M23.3333 9.33337V2.33337H10.4999L6.99992 5.83337L4.66658 8.16671V25.6667H9.33325M10.4999 2.33337V8.16671H4.66658" stroke="black" strokeWidth="1.16667"/>
		<circle cx="18.6666" cy="18.6667" r="5.83333" stroke="black" strokeWidth="1.16667"/>
		<path d="M22.7499 22.75L26.2499 26.25" stroke="black" strokeWidth="1.16667"/>
	</svg>
);

// 양도 아이콘 (사람 + 양방향 화살표)
const IconPeopleChange = () => (
	<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28" fill="none">
		<circle cx="14.0001" cy="8.16671" r="5.83333" stroke="black" strokeWidth="1.16667"/>
		<path d="M3.5 25.0833V23.9167C3.5 18.4398 7.93984 14 13.4167 14H14.5833C16.9396 14 19.1039 14.8218 20.8053 16.1944" stroke="black" strokeWidth="1.16667"/>
		<path d="M16.3333 19.8333H25.6666M23.3333 21.7455L25.6666 19.8333L23.3333 17.5" stroke="black" strokeWidth="1.16667"/>
		<path d="M25.6667 24.5H16.3334M18.6667 26.4121L16.3334 24.5L18.6667 22.1666" stroke="black" strokeWidth="1.16667"/>
	</svg>
);

/* ───────────── styled-components ───────────── */

const Wrap = styled.div`
	flex: 1;
	display: flex;
	flex-direction: column;
`;

const Header = styled.header`
	padding-top: 130px;
	padding-bottom: 80px;

	${media.mobile`
		padding: 80px 20px 25px;
	`}
`;

const HeaderInner = styled.div`
	width: 1200px;
	margin: 0 auto;
	display: flex;
	flex-direction: column;
	gap: 10px;

	${media.mobile`
		width: 100%;
		gap: 5px;
	`}
`;

const Logo = styled.div`
	width: 62px;

	img {
		display: block;
		width: 100%;
	}

	${media.mobile`
		width: 30px;
	`}
`;

const EventTitle = styled.h2`
	${mixins.fontStyle("40px", "600", "54px")}
	color: #000;
	margin: 0;

	${media.mobile`
		${mixins.fontStyle("20px", "600", "27px")}
	`}
`;

const Content = styled.div`
	flex: 1;
	display: flex;
	justify-content: center;
	align-items: flex-start;
	padding: 68px 20px;
	background-color: #fff;

	${media.mobile`
		padding: 60px 20px 10px;
	`}
`;

const MenuList = styled.div`
	width: 700px;
	display: flex;
	flex-direction: column;
	gap: 24px;

	${media.mobile`
		width: 100%;
		gap: 12px;
	`}
`;

const MenuCard = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	height: 100px;
	background-color: #f5f5f5;
	border-radius: 12px;
	padding: 4px 40px;
	overflow: hidden;

	${media.mobile`
		height: 64px;
		padding: 4px 20px;
	`}
`;

const MenuInfo = styled.div`
	display: flex;
	align-items: center;
	gap: 16px;

	.menu-icon {
		width: 28px;
		height: 28px;
		flex-shrink: 0;
		display: flex;
		align-items: center;
		justify-content: center;

		svg {
			width: 100%;
			height: 100%;
		}
	}

	.menu-label {
		${mixins.fontStyle("26px", "500", "normal")}
		color: #000;
		white-space: nowrap;
	}

	${media.mobile`
		gap: 10px;

		.menu-icon {
			width: 24px;
			height: 24px;
		}

		.menu-label {
			${mixins.fontStyle("16px", "600", "normal")}
		}
	`}
`;

const BtnShortcut = styled.button`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 91px;
	height: 42px;
	padding: 13px 16px 10px 16px;
	background-color: #06315b;
	color: #fff;
	border-radius: 100px;
	border: none;
	cursor: pointer;
	${mixins.fontStyle("16px", "500", "normal")}
	flex-shrink: 0;
	font-family: 'Noto Sans KR', sans-serif;

	&:hover {
		opacity: 0.9;
	}

	${media.mobile`
		width: auto;
		height: auto;
		padding: 8px 12px;
		${mixins.fontStyle("14px", "500", "normal")}
	`}
`;

const MenuItemWrap = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	width: 100%;
`;

const TooltipWrap = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	margin-top: -4px;
`;

const TooltipArrow = styled.div`
	width: 0;
	height: 0;
	border-left: 13.5px solid transparent;
	border-right: 13.5px solid transparent;
	border-bottom: 12px solid #e6eaef;
`;

const TooltipContent = styled.div`
	background-color: #e6eaef;
	border-radius: 12px;
	padding: 12px 20px;
	${mixins.fontStyle("18px", "400", "normal")}
	color: #000;
	white-space: nowrap;

	${media.mobile`
		${mixins.fontStyle("14px", "400", "normal")}
	`}
`;

/* ───────────── 대시보드 컴포넌트 (독립 라우트 페이지) ───────────── */

const Dashboard = () => {
	const { idx } = useParams();
	const navigate = useNavigate();
	const dispatch = useDispatch();

	// URL에서 이벤트 정보 추출 (기존 Application.jsx와 동일한 방식)
	const idxParams = new URLSearchParams("?" + window.atob(idx));
	const scheduleIdx = idxParams.get("scheduleIdx");

	const customersInfo = useSelector((state) => state.customers).value;
	const primaryCustomer = customersInfo.filter((v) => v.isPrimary)[0];

	// 행사 정보 조회
	const scheduleQuery = useScheduleQuery(scheduleIdx, primaryCustomer ? primaryCustomer.uid : "");

	// 양도받은 액티비티 조회 (툴팁 표시 여부)
	const transferredActivityListQuery = useTransferredActivityList(scheduleIdx, primaryCustomer?.uid);
	const hasTransferredActivity = transferredActivityListQuery.data?.data?.length > 0;

	// 로딩 중
	if (scheduleQuery.isLoading) {
		return (
			<PageTemplate title="">
				<Loading layer={true} />
			</PageTemplate>
		);
	}

	// 에러 처리 — scheduleQuery 에러 시 원칙적으로 전체 차단이나, 양도 기간이면 예외 허용
	let scheduleData = null;
	let isPeriodClosed = false;

	if (scheduleQuery.isError) {
		const errorData = scheduleQuery.error?.response?.data?.data;
		
		// 403 에러가 나더라도 양도/양수 가능 기간이면 대시보드 렌더링을 허용
		if (errorData?.activity_transfer_visible) {
			scheduleData = errorData;
			isPeriodClosed = true;
		} else {
			const title = errorData?.title || "";
			const order = errorData?.order || "";
			const errorMsg = scheduleQuery.error?.response?.data?.msg?.ko || "오류가 발생했습니다.";
			return (
				<PageTemplate title={(title && order) ? `${title} – ${order}차수` : ""}>
					<Error msg={errorMsg} />
				</PageTemplate>
			);
		}
	} else if (scheduleQuery.data) {
		scheduleData = scheduleQuery.data;
	}

	if (!scheduleData) return null;

	const applicationIdx = scheduleData.application_idx;
	const title = scheduleData?.title || "";
	const order = scheduleData?.order || "";
	const isRestricted = scheduleData?.restricted === true;

	// 양도 버튼 노출: activity_transfer_visible이 있으면 우선 사용
	// 없으면 기존 로직 (시크릿 미설정 행사 하위호환)
	const isTransferEnabled = scheduleData?.activity_transfer_visible !== undefined
		? scheduleData.activity_transfer_visible
		: (scheduleData?.target === "activity" && Number(scheduleData?.activity_transfer_enabled) === 1);

	/* ─── 버튼 클릭 핸들러 ─── */

	// 신청하기
	const handleApply = () => {
		if (isRestricted) {
			dispatch(showAlert({ msg: scheduleData.restrictionMessage, btnText: "확인" }));
			return;
		}
		if (applicationIdx) {
			dispatch(showAlert({ msg: "이미 신청 내역이 있습니다.", btnText: "확인" }));
		} else {
			navigate(`/application/${idx}`);
		}
	};

	// 신청조회 및 수정
	const handleInquiry = () => {
		if (isRestricted) {
			dispatch(showAlert({ msg: scheduleData.restrictionMessage, btnText: "확인" }));
			return;
		}
		if (applicationIdx) {
			navigate(`/application/complete/${applicationIdx}/${scheduleIdx}`);
		} else {
			dispatch(showAlert({ msg: "신청 내역이 없습니다.", btnText: "확인" }));
		}
	};

	// 액티비티 양도
	const handleTransfer = () => {
		navigate(`/application/${idx}/activityTransfer`);
	};

	return (
		<Wrap>
			<Header>
				<HeaderInner>
					<Logo>
						<img src={process.env.PUBLIC_URL + "/image/img_logo_amway.svg"} alt="Amway" />
					</Logo>
					{title && order && (
						<EventTitle>
							{title} – {order}차수
						</EventTitle>
					)}
				</HeaderInner>
			</Header>

			<Content>
				<MenuList>
					{/* 신청하기 / 신청조회 및 수정 — 신청 기간이 닫혔을 때는 숨김 */}
					{!isPeriodClosed && (
						<>
							<MenuCard>
								<MenuInfo>
									<div className="menu-icon"><IconDocumentApply /></div>
									<span className="menu-label">신청하기</span>
								</MenuInfo>
								<BtnShortcut onClick={handleApply}>바로가기</BtnShortcut>
							</MenuCard>

							<MenuCard>
								<MenuInfo>
									<div className="menu-icon"><IconDocumentSearch /></div>
									<span className="menu-label">신청조회 및 수정</span>
								</MenuInfo>
								<BtnShortcut onClick={handleInquiry}>바로가기</BtnShortcut>
							</MenuCard>
						</>
					)}

					{/* 액티비티 양도 - activity_transfer_enabled === 1일 때만 노출 */}
					{isTransferEnabled && (
						<>
							<MenuCard>
								<MenuInfo>
									<div className="menu-icon"><IconPeopleChange /></div>
									<span className="menu-label">액티비티 양도</span>
								</MenuInfo>
								<BtnShortcut onClick={handleTransfer}>바로가기</BtnShortcut>
							</MenuCard>

							{hasTransferredActivity && (
								<TooltipWrap>
									<TooltipArrow />
									<TooltipContent>양도 받을 액티비티 횟수가 있어요</TooltipContent>
								</TooltipWrap>
							)}
						</>
					)}
				</MenuList>
			</Content>
		</Wrap>
	);
};

export default Dashboard;
